/**
 * Adding and removing the CONNECTIONS a requirement takes part in.
 *
 * Not part of EntityForm, and that is not an oversight. Every field the
 * generic form writes is an attribute binding sent as one PATCH of the whole
 * declaration. A source is a `SourceOf` connection written inside the
 * requirement and a cross reference is a `CrossReference` written at package
 * level — neither is a field, both are their own route, and each change is its
 * own write. So the form shows them read-only (`derived`) and this edits them.
 *
 * ONE ROW AT A TIME, deliberately: there is no "save" here. Adding a source
 * regenerates the .sysml and commits, exactly as the API does it, because a
 * connection is a declaration rather than a value on one.
 */

import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import { ApiError } from '../../api'
import type { Schema } from '../../schema'
import ReferencePicker from './ReferencePicker'

export interface LinkEditorProps {
  schema: Schema
  /** What the links are called on screen, e.g. 'Sources'. */
  label: string
  /** One line saying what the links MEAN, since the two kinds differ. */
  help: string
  /** The entity kind the far end must be. */
  target: string
  /** The identifiers this requirement is already linked to. */
  value: string[]
  /** Whether the signed-in role may write at all. */
  allowed: boolean
  /** Why not, when it may not. */
  reason?: string
  onAdd: (identifier: string) => Promise<unknown>
  onRemove: (identifier: string) => Promise<unknown>
  /** Re-read the row, because a write regenerates the file. */
  onChanged: () => void
}

export default function LinkEditor({
  schema,
  label,
  help,
  target,
  value,
  allowed,
  reason,
  onAdd,
  onRemove,
  onChanged,
}: LinkEditorProps) {
  const [chosen, setChosen] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Run one write, and put the server's own words on screen if it refuses.
   *
   * The refusals are the interesting half — a cycle, a duplicate, a
   * requirement naming itself — and every one of them is a rule the server
   * knows and this component does not. Restating them here would mean two
   * places to keep in step, so the message is passed through as it arrives.
   */
  const run = (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    work()
      .then(() => {
        setChosen('')
        onChanged()
      })
      .catch((failure: ApiError) => setError(failure.message))
      .finally(() => setBusy(false))
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">{label}</Typography>
      <Typography variant="caption" color="text.secondary">
        {help}
      </Typography>

      {value.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          None stated.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {value.map((identifier) => (
            <Chip
              key={identifier}
              label={identifier}
              size="small"
              onDelete={allowed && !busy ? () => run(() => onRemove(identifier)) : undefined}
            />
          ))}
        </Box>
      )}

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <ReferencePicker
            schema={schema}
            target={target}
            label={`Add ${label.toLowerCase().replace(/s$/, '')}`}
            disabled={!allowed || busy}
            helperText={allowed ? undefined : reason}
            value={chosen || null}
            onChange={(next) => setChosen(typeof next === 'string' ? next : '')}
          />
        </Box>
        <Button
          startIcon={<AddIcon />}
          disabled={!allowed || busy || !chosen}
          onClick={() => run(() => onAdd(chosen))}
          sx={{ mt: 1 }}
        >
          Add
        </Button>
      </Stack>
    </Stack>
  )
}
